import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Loader2, ArrowLeft, Receipt, Calendar, CreditCard, Layers, ChevronDown, User } from 'lucide-react';
import { useToast } from '../hooks/useToast';
import { fetchInvoicesHistory, fetchParentWardProfile } from "../apiHelper";

const Invoices = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { showToast } = useToast();

    // Capture state values from routing context
    const [selectedWard, setSelectedWard] = useState(location.state?.ward || null);
    const [wardsList, setWardsList] = useState([]);
    const [loadingWards, setLoadingWards] = useState(false);
    const [loadingInvoices, setLoadingInvoices] = useState(false);
    const [invoiceData, setInvoiceData] = useState(null);

    useEffect(() => {
        if (location.state?.ward) return; // Context exists from Dashboard routing path

        const loadAvailableWards = async () => {
            setLoadingWards(true);
            try {
                const profile = await fetchParentWardProfile();
                if (profile?.wards && profile.wards.length > 0) {
                    setWardsList(profile.wards);
                    setSelectedWard(profile.wards[0]); // Default automatically to the first ward item
                } else {
                    showToast("No connected ward profiles found for this account.", "warning");
                }
            } catch (err) {
                console.error("Error fetching ward profiles:", err);
                showToast("Failed to initialize parental ward context options.", "error");
            } finally {
                setLoadingWards(false);
            }
        };

        loadAvailableWards();
    }, [location.state, showToast]);

    useEffect(() => {
        if (!selectedWard?.member_id) {
            setInvoiceData(null);
            return;
        }

        const loadInvoiceHistory = async () => {
            setLoadingInvoices(true);
            try {
                const payload = {
                    customer_id: selectedWard.member_id,
                    as_of_date: new Date().toISOString()
                };

                const results = await fetchInvoicesHistory(payload);
                setInvoiceData(results);
            } catch (err) {
                console.error("Error fetching invoice history:", err);
                showToast(err.response?.data?.detail || "Could not fetch invoice statement history.", "error");
                setInvoiceData(null);
            } finally {
                setLoadingInvoices(false);
            }
        };

        loadInvoiceHistory();
    }, [selectedWard, showToast]);

    // Helper utility for currency display mapping
    const formatCurrency = (amount, currencyCode = "NGN") => {
        const symbolMap = { NGN: "₦", USD: "$", GBP: "£", EUR: "€" };
        const symbol = symbolMap[currencyCode] || currencyCode + " ";
        return `${symbol}${(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    // Helper utility to clean up systemic status badges
    const getStatusBadge = (status) => {
        const config = {
            HLD: { text: "Invoiced", classes: "bg-amber-50 text-amber-700 border-amber-200" },
            OUT: { text: "On Hold", classes: "bg-amber-50 text-amber-700 border-amber-200" },
            VOD: { text: "Voided", classes: "bg-red-50 text-red-700 border-red-200" },
            PEND: { text: "Pending", classes: "bg-blue-50 text-blue-700 border-blue-200" }
        };
        const current = config[status] || { text: status, classes: "bg-zinc-50 text-zinc-700 border-zinc-200" };
        return <span className={`px-2.5 py-1 text-xs font-semibold rounded-md border ${current.classes}`}>{current.text}</span>;
    };

    if (loadingWards) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[50vh] gap-3">
                <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
                <p className="text-sm font-medium text-zinc-500">Initializing ward accounts...</p>
            </div>
        );
    }

    const orders = invoiceData?.orders || [];
    const totalOutstanding = invoiceData?.total_receivable_balance || 0;

    return (
        <div className="p-6 sm:p-8 max-w-5xl mx-auto space-y-6">
            {/* Top Navigation Bar */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-zinc-100 pb-4">
                <button
                    onClick={() => navigate(-1)}
                    className="flex items-center gap-2 text-sm font-semibold text-zinc-600 hover:text-zinc-900 transition-colors cursor-pointer"
                >
                    <ArrowLeft size={16} /> Back
                </button>
                {!location.state?.ward && wardsList.length > 0 && (
                    <div className="relative inline-block w-full sm:w-72">
                        <label className="block text-[10px] uppercase font-bold text-zinc-400 mb-1">Switch Ward Account</label>
                        <div className="relative">
                            <select
                                value={selectedWard?.member_id || ""}
                                onChange={(e) => {
                                    const targetWard = wardsList.find(w => w.member_id === parseInt(e.target.value));
                                    if (targetWard) setSelectedWard(targetWard);
                                }}
                                className="w-full bg-white border border-zinc-200 text-zinc-800 text-sm font-medium py-2 pl-9 pr-10 rounded-xl appearance-none focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all shadow-sm cursor-pointer"
                            >
                                {wardsList.map((w) => (
                                    <option key={w.member_id} value={w.member_id}>
                                        {w.first_name} {w.last_name}
                                    </option>
                                ))}
                            </select>
                            <User size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
                            <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
                        </div>
                    </div>
                )}
                {/* {invoiceData?.as_of_date && (
                    <span className="text-xs text-zinc-400">
                        As of: {new Date(invoiceData.as_of_date).toLocaleString()}
                    </span>
                )} */}
            </div>

            {!selectedWard ? (
                <div className="bg-white border border-zinc-200 rounded-xl p-8 text-center text-zinc-500 shadow-sm">
                    <p className="text-sm font-medium">Please select a ward to view corresponding invoice histories.</p>
                </div>
            ) : loadingInvoices ? (
                <div className="flex flex-col items-center justify-center min-h-[30vh] gap-3">
                    <Loader2 className="w-6 h-6 text-indigo-600 animate-spin" />
                    <p className="text-xs font-medium text-zinc-400">Loading invoice records...</p>
                </div>
            ) : (
                <>
                    {/* Total Balance Overview Summary Card */}
                    <div className="bg-zinc-900 text-white rounded-2xl p-6 sm:p-8 shadow-md border border-zinc-800 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div>
                            <p className="text-sm font-medium text-zinc-400 uppercase tracking-wider">Total Amount Payable</p>
                            <h1 className="text-3xl sm:text-4xl font-black mt-1 tracking-tight">
                                {formatCurrency(totalOutstanding, orders[0]?.currency)}
                            </h1>
                        </div>
                        <div className="text-sm sm:text-right text-zinc-300 border-t sm:border-t-0 border-zinc-800 pt-4 sm:pt-0">
                            <p className="font-semibold text-white">
                                {selectedWard.first_name} {selectedWard.middle_name ? `${selectedWard.middle_name} ` : ''}{selectedWard.last_name}
                            </p>
                            <p className="text-xs text-zinc-400 mt-0.5">ID Ref: #{selectedWard.member_id}</p>
                        </div>
                    </div>
                </>
            )}

            {/* Invoices List Stack Container */}
            <div className="space-y-4">
                <div className="flex items-center justify-between border-b border-zinc-100 pb-2">
                    <h2 className="text-md font-bold text-zinc-800 flex items-center gap-2">
                        <Receipt size={18} className="text-zinc-500" /> Statement History ({orders.length})
                    </h2>
                </div>

                {orders.length === 0 ? (
                    <div className="bg-white border border-zinc-200 rounded-xl p-8 text-center text-zinc-500 shadow-sm">
                        <p className="text-sm font-medium">No historic orders or standard statements found for this profile.</p>
                    </div>
                ) : (
                    orders.map((order) => (
                        <div key={order.orders_id} className="bg-white border border-zinc-200 rounded-xl shadow-sm overflow-hidden hover:border-zinc-300 transition-all">
                            {/* Card Header Structure */}
                            <div className="p-4 sm:p-6 border-b border-zinc-100 bg-zinc-50/50 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                <div className="space-y-1">
                                    <div className="flex items-center gap-3 flex-wrap">
                                        <h3 className="text-sm font-bold text-zinc-900 tracking-tight">
                                            {order.idempotency_key || `Order ID: ${order.orders_id}`}
                                        </h3>
                                        {getStatusBadge(order.status)}
                                    </div>
                                    <p className="text-xs text-zinc-500">{order.store?.identifier || order.organization?.organization_name}</p>
                                </div>
                                <div className="text-right sm:self-center">
                                    <span className="text-lg font-black text-zinc-900">
                                        {formatCurrency(order.totalproduct, order.currency)}
                                    </span>
                                </div>
                            </div>

                            {/* Card Details Body */}
                            <div className="p-4 sm:p-6 space-y-4">
                                {order.comments && (
                                    <p className="text-xs font-medium text-zinc-600 bg-zinc-50 border border-zinc-100 px-3 py-2 rounded-lg">
                                        <span className="font-bold text-zinc-700">Memo:</span> {order.comments}
                                    </p>
                                )}

                                {/* Contextual Metadata Metadata Fields Row */}
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-xs pt-1">
                                    <div className="flex items-center gap-2 text-zinc-600">
                                        <Calendar size={14} className="text-zinc-400" />
                                        <div>
                                            <p className="text-[10px] text-zinc-400 uppercase font-bold">Issued On</p>
                                            <p className="font-semibold text-zinc-800">
                                                {/* {new Date(order.placed_on).toLocaleDateString()} */}
                                                {new Date(order.placed_on).toLocaleDateString(undefined, {
                                                    year: 'numeric',
                                                    month: 'long',
                                                    day: 'numeric'
                                                })}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 text-zinc-600">
                                        <CreditCard size={14} className="text-zinc-400" />
                                        <div>
                                            <p className="text-[10px] text-zinc-400 uppercase font-bold">Amount Paid</p>
                                            <p className="font-semibold text-zinc-800">{formatCurrency(order.amountpaid, order.currency)}</p>
                                        </div>
                                    </div>
                                    <div className="col-span-2 md:col-span-1 flex items-center gap-2 text-zinc-600">
                                        <Layers size={14} className="text-zinc-400" />
                                        <div>
                                            <p className="text-[10px] text-zinc-400 uppercase font-bold">Account Description</p>
                                            <p className="font-semibold text-zinc-800 truncate max-w-[220px]">{order.description}</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Order Items Sub-Table Context */}
                                {order.orderitems && order.orderitems.length > 0 && (
                                    <div className="mt-4 pt-4 border-t border-zinc-100">
                                        <p className="text-[11px] font-bold uppercase tracking-wider text-zinc-400 mb-2">Itemized Breakdown</p>
                                        <div className="overflow-x-auto">
                                            <table className="w-full text-left text-xs border-collapse">
                                                <thead>
                                                    <tr className="border-b border-zinc-100 text-zinc-400 font-medium">
                                                        <th className="pb-2 font-semibold">Item Specification</th>
                                                        <th className="pb-2 text-center font-semibold w-16">Qty</th>
                                                        <th className="pb-2 text-right font-semibold w-28">Unit Price</th>
                                                        <th className="pb-2 text-right font-semibold w-28">Total Amount</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-zinc-50">
                                                    {order.orderitems.map((item) => (
                                                        <tr key={item.orderitems_id} className="text-zinc-700 hover:bg-zinc-50/50">
                                                            <td className="py-2.5 font-medium text-zinc-800">
                                                                {item.catentry?.productname || "Standard Registration Line Item"}
                                                                <span className="block text-[10px] font-normal text-zinc-400 mt-0.5 font-mono">{item.catentry?.partnumber}</span>
                                                            </td>
                                                            <td className="py-2.5 text-center text-zinc-500 font-medium">{item.quantity}</td>
                                                            <td className="py-2.5 text-right font-medium">{formatCurrency(item.price, item.currency)}</td>
                                                            <td className="py-2.5 text-right font-bold text-zinc-900">{formatCurrency(item.totalproduct, item.currency)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))
                )}
            </div>
            {/* Floating Action Payment Hook Trigger */}
            {selectedWard && (
                <div className="fixed bottom-6 right-6 z-40 animate-in fade-in slide-in-from-bottom-4 duration-300">
                    <button
                        onClick={() => navigate('/dashboard/new-payment', { state: { ward: selectedWard } })}
                        className="flex items-center gap-2 px-5 py-3.5 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-sm rounded-full shadow-xl hover:shadow-indigo-500/20 hover:-translate-y-0.5 transition-all cursor-pointer group"
                    >
                        <CreditCard size={16} className="transition-transform group-hover:scale-110" />
                        <span>Make Payment</span>
                    </button>
                </div>
            )}
        </div>
    );
};

export default Invoices;